import { useCallback } from 'react';
import tw from 'twin.macro';
import { useTranslation } from 'react-i18next';

import { PlayIcon } from '@icons/PlayIcon';
import { ExportIcon } from '@icons/ExportIcon';
import { Loader } from '@components/Loader';
import { Button } from '@components/Button';
import { download } from '@utils/download';
import { convertSecondsToHMS } from '@utils/dateUtils';
import { useEpisodeDetailsContext } from '@insights_module/context/EpisodeDetailsContext';
import { useInsightsContext } from '@insights_module/context/InsightsContext';
import { useEpisodeDetailsData } from '@insights_module/hooks/useEpisodeDetailsData';
import { getChapterTitle } from '@utils/chapters';

const CardItem = tw.div`flex justify-between h-[40px] w-full items-center text-[16px] text-text-and-icon font-bold px-[16px] border-b border-gray-3`;
const TimeBadge = tw.div`flex items-center justify-center rounded-full bg-sec-4 text-accent-1 text-sm font-medium px-[12px] py-[2px] gap-[12px] cursor-pointer`;
const ChapterLabel = tw.div`font-medium`;
const DownloadButton = tw(Button.Primary)`flex justify-center items-center min-h-[40px] px-[14px]`;
const LoaderWrapper = tw.div`flex justify-center items-center h-[200px] w-full`;

export const EpisodesChaptersItem = () => {
  const { selectedEpisodeDetails, isLoading } = useEpisodeDetailsData();
  const { activeView, setSelectedChapterTime } = useEpisodeDetailsContext();

  if (activeView !== 'chapters') return null;

  return (
    <>
      {isLoading && (
        <LoaderWrapper>
          <Loader size='lg' theme='dark' />
        </LoaderWrapper>
      )}
      {selectedEpisodeDetails?.chapters?.map((item, index) => (
        <CardItem key={`${item.start_time}-${item.end_time}`}>
          <ChapterLabel>{getChapterTitle(item.title_suggestions, index)}</ChapterLabel>
          <TimeBadge onClick={() => setSelectedChapterTime(item.start_time)}>
            {`${convertSecondsToHMS(item.start_time)}-${convertSecondsToHMS(item.end_time)}`}
            <PlayIcon tw='fill-accent-1 -mr-[2px]' />
          </TimeBadge>
        </CardItem>
      ))}
    </>
  );
};

export const DownloadChaptersButton = () => {
  const { t } = useTranslation();
  const { activeView } = useEpisodeDetailsContext();
  const { selectedEpisode } = useInsightsContext();
  const { selectedEpisodeDetails, isLoading } = useEpisodeDetailsData();

  const handleClickDownload = useCallback(() => {
    if (isLoading || !selectedEpisodeDetails?.chapters) return;

    const file = new Blob(
      [
        selectedEpisodeDetails.chapters
          .map((item, index) => `Chapter${index + 1} ${item.start_time} - ${item.end_time}`)
          .join('\n'),
      ],
      { type: 'text/plain' }
    );
    const fileName = `chapters-${selectedEpisode}.txt`;

    download(file, fileName);
  }, [selectedEpisodeDetails?.chapters]);

  if (activeView !== 'chapters') return null;

  return (
    <DownloadButton
      onClick={handleClickDownload}
      disabled={isLoading || !selectedEpisodeDetails?.chapters?.length}>
      <ExportIcon tw='stroke-text-and-icon-invert' />
      {t('insights.episode_details_drawer.download_button.chapters')}
    </DownloadButton>
  );
};
